import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Pencil, Trash2, Plus, RefreshCw, Shield, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { BulkBar, RowCheck } from "./BulkBar";

export function AdminGuardsTab({ buildings = [] }) {
  const [guards, setGuards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", building_id: "", building_ids: [],
  });
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);

  // --- bulk selection ----------------------------------------------------
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDelete, setBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/users", { params: { role: "guard" } });
      setGuards(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load guards");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function openNew() {
    setForm({ name: "", email: "", password: "", phone: "", building_id: "", building_ids: [] });
    setEditing("new");
  }
  function openEdit(g) {
    setForm({
      name: g.name || "", email: g.email || "", password: "",
      phone: g.phone || "", building_id: g.building_id || "",
      building_ids: g.building_ids || (g.building_id ? [g.building_id] : []),
    });
    setEditing(g);
  }

  function toggleBuilding(bid) {
    setForm((prev) => {
      const has = prev.building_ids.includes(bid);
      return { ...prev, building_ids: has ? prev.building_ids.filter((x) => x !== bid) : [...prev.building_ids, bid] };
    });
  }

  async function save() {
    setSaving(true);
    try {
      if (editing === "new") {
        const body = {
          role: "guard",
          name: form.name, email: form.email, password: form.password,
          phone: form.phone || null,
          building_id: form.building_id || null,
          building_ids: form.building_ids,
        };
        await api.post("/platform/users", body);
        toast.success("Guard created");
      } else {
        const body = {};
        if (form.name !== editing.name) body.name = form.name;
        if (form.email !== editing.email) body.email = form.email;
        if (form.password) body.password = form.password;
        if (form.phone !== (editing.phone || "")) body.phone = form.phone;
        if ((form.building_id || "") !== (editing.building_id || "")) body.building_id = form.building_id;
        body.building_ids = form.building_ids;
        await api.patch(`/platform/users/${editing.id}`, body);
        toast.success("Guard updated");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function toggleActive(g) {
    try {
      await api.patch(`/platform/users/${g.id}`, { is_active: !g.is_active });
      toast.success(g.is_active ? "Guard deactivated" : "Guard activated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  }

  async function doDelete() {
    try {
      await api.delete(`/platform/users/${toDelete.id}`);
      toast.success("Guard deleted");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  // --- bulk helpers ------------------------------------------------------
  function toggleRow(id, on) {
    setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  }
  function toggleAllOnPage(on) {
    setSelected(() => (on ? new Set(guards.map((g) => g.id)) : new Set()));
  }
  function clearSelection() { setSelected(new Set()); }

  async function runBulkActive(is_active) {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/guards/set-active", {
        ids: Array.from(selected), is_active,
      });
      const skippedN = data.skipped?.length || 0;
      toast.success(`${data.updated} ${is_active ? "activated" : "deactivated"}${skippedN ? ` · ${skippedN} skipped` : ""}`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk toggle failed");
    } finally { setBulkBusy(false); }
  }

  async function runBulkDelete() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/users/delete", { ids: Array.from(selected) });
      const skippedN = data.skipped?.length || 0;
      toast.success(`${data.deleted} deleted${skippedN ? ` · ${skippedN} skipped` : ""}`);
      setBulkDelete(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  }

  const allOnPageChecked = guards.length > 0 && guards.every((g) => selected.has(g.id));

  return (
    <div className="space-y-4" data-testid="admin-guards-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Shield size={14}/> {guards.length} guard{guards.length !== 1 ? "s" : ""}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="guards-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
          </Button>
          <Button onClick={openNew} data-testid="guards-new">
            <Plus size={14} className="mr-1.5"/> New guard
          </Button>
        </div>
      </div>

      <div className="card-stroke p-0 overflow-x-auto">
        <BulkBar count={selected.size} onClear={clearSelection} testId="guards-bulk-bar">
          <Button size="sm" variant="outline" onClick={() => runBulkActive(true)}
                  disabled={bulkBusy} data-testid="guards-bulk-activate">
            <Power size={14} className="mr-1.5"/> Activate
          </Button>
          <Button size="sm" variant="outline" onClick={() => runBulkActive(false)}
                  disabled={bulkBusy} data-testid="guards-bulk-deactivate">
            <PowerOff size={14} className="mr-1.5"/> Deactivate
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBulkDelete(true)}
                  className="text-destructive hover:text-destructive"
                  data-testid="guards-bulk-delete">
            <Trash2 size={14} className="mr-1.5"/> Delete
          </Button>
        </BulkBar>
        <table className="w-full text-sm" data-testid="guards-table">
          <thead>
            <tr className="label-tiny text-left border-b border-border">
              <th className="py-3 pl-4 pr-2 w-10">
                <RowCheck checked={allOnPageChecked} onChange={toggleAllOnPage}
                          testId="guards-select-all" label="Select all guards"/>
              </th>
              <th>Name</th><th>Email</th><th>Buildings</th>
              <th>Active</th><th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {guards.length === 0 ? (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground" data-testid="guards-empty">
                {loading ? "Loading…" : "No guards yet."}
              </td></tr>
            ) : guards.map((g) => {
              const bids = (g.building_ids && g.building_ids.length) ? g.building_ids
                           : (g.building_id ? [g.building_id] : []);
              return (
                <tr key={g.id} className="border-b border-border last:border-0" data-testid={`guard-row-${g.id}`}>
                  <td className="py-3 pl-4 pr-2">
                    <RowCheck checked={selected.has(g.id)} onChange={(on)=>toggleRow(g.id, on)}
                              testId={`guard-select-${g.id}`}/>
                  </td>
                  <td>{g.name}</td>
                  <td className="font-mono text-xs">{g.email}</td>
                  <td className="text-xs">
                    {bids.length === 0 ? <span className="text-muted-foreground">—</span>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {bids.map((bid) => {
                          const b = buildings.find((x) => x.id === bid);
                          return (
                            <span key={bid} className="font-mono px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full">
                              {b?.code || bid.slice(0, 6)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td>
                    <Switch
                      checked={g.is_active !== false}
                      onCheckedChange={()=>toggleActive(g)}
                      data-testid={`guard-active-${g.id}`}
                    />
                  </td>
                  <td className="text-right pr-4 whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={()=>openEdit(g)}
                            data-testid={`guard-edit-${g.id}`} aria-label="Edit"><Pencil size={14}/></Button>
                    <Button size="icon" variant="ghost" onClick={()=>setToDelete(g)}
                            data-testid={`guard-delete-${g.id}`} aria-label="Delete">
                      <Trash2 size={14} className="text-destructive"/>
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o)=>{ if (!o) setEditing(null); }}>
        <DialogContent className="max-w-xl" data-testid="guard-modal">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Create guard" : `Edit ${editing?.name}`}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-tiny">Name</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2" data-testid="guard-form-name"/></div>
            <div><Label className="label-tiny">Phone</Label>
              <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="mt-2" data-testid="guard-form-phone"/></div>
            <div><Label className="label-tiny">Email</Label>
              <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-2 font-mono text-xs" data-testid="guard-form-email"/></div>
            <div><Label className="label-tiny">Password {editing !== "new" && "(leave blank to keep)"}</Label>
              <Input type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="mt-2 font-mono" data-testid="guard-form-password"/></div>
            <div className="col-span-2">
              <Label className="label-tiny">Primary building</Label>
              <Select value={form.building_id || "none"} onValueChange={(v)=>setForm({...form, building_id: v==="none"?"":v})}>
                <SelectTrigger className="mt-2" data-testid="guard-form-building"><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {buildings.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="label-tiny">Also covers (multi-select)</Label>
              <div className="mt-2 border border-border rounded-md p-3 max-h-40 overflow-y-auto space-y-2"
                   data-testid="guard-form-buildings">
                {buildings.length === 0 && (
                  <div className="text-xs text-muted-foreground">No buildings available.</div>
                )}
                {buildings.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.building_ids.includes(b.id)}
                      onChange={()=>toggleBuilding(b.id)}
                      data-testid={`guard-form-building-${b.id}`}
                    />
                    <span>{b.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{b.code}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditing(null)} data-testid="guard-form-cancel">Cancel</Button>
            <Button onClick={save}
                    disabled={saving || !form.name || !form.email || (editing === "new" && !form.password)}
                    data-testid="guard-form-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <DialogContent data-testid="guard-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete guard {toDelete?.name}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            This permanently removes the guard account. This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setToDelete(null)} data-testid="guard-delete-cancel">Cancel</Button>
            <Button onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="guard-delete-confirm">Delete guard</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDelete} onOpenChange={setBulkDelete}>
        <DialogContent data-testid="guards-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              Delete {selected.size} guard{selected.size === 1 ? "" : "s"}?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Self and the bootstrap super admin are skipped automatically. This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkDelete(false)} data-testid="guards-bulk-delete-cancel">Cancel</Button>
            <Button onClick={runBulkDelete} disabled={bulkBusy}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="guards-bulk-delete-confirm">
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
